import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedIcon, IconName } from '../../components/icons';
import { Conversation, ConversationType, UserRole } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../theme';

interface ChatInboxProps {
  conversations: Conversation[];
  basePath: string;                      // e.g. '/client', '/surveyor', '/admin'
  title?: string;
  subtitle?: string;
  showModeration?: boolean;
  onBlockUser?: (conversationId: string) => void;
  onUnblockUser?: (conversationId: string) => void;
}

const TYPE_META: Record<ConversationType, { label: string; icon: IconName; color: string }> = {
  REQUEST: { label: 'Project', icon: 'chat', color: 'var(--sc-brand-primary)' },
  CLIENT_SUPPORT: { label: 'Client Support', icon: 'user', color: 'var(--sc-warning)' },
  SURVEYOR_SUPPORT: { label: 'Surveyor Support', icon: 'worker', color: '#8B5CF6' },
};

const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'All Messages' },
  { value: 'REQUEST', label: 'Projects' },
  { value: 'CLIENT_SUPPORT', label: 'Client Support' },
  { value: 'SURVEYOR_SUPPORT', label: 'Surveyor Support' },
];

const PRESENCE: Record<string, { color: string; label: string }> = {
  online:  { color: 'var(--sc-success)', label: 'Online' },
  away:    { color: 'var(--sc-warning)', label: 'Away' },
  offline: { color: 'var(--sc-text-tertiary)', label: 'Offline' },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export const ChatInbox: React.FC<ChatInboxProps> = ({
  conversations,
  basePath,
  title = 'Messages',
  subtitle = 'Your conversations',
  showModeration = false,
  onBlockUser,
  onUnblockUser,
}) => {
  const { user } = useAuthStore();
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const { isDark } = useTheme();

  const availableFilters = useMemo(() => {
    if (user?.role === UserRole.CLIENT) return FILTER_OPTIONS.filter(f => f.value !== 'SURVEYOR_SUPPORT');
    if (user?.role === UserRole.SURVEYOR) return FILTER_OPTIONS.filter(f => f.value !== 'CLIENT_SUPPORT');
    return FILTER_OPTIONS;
  }, [user?.role]);

  const filtered = useMemo(() => {
    let list = conversations;
    if (filter !== 'ALL') list = list.filter(c => c.type === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.subject?.toLowerCase().includes(q) ||
        (c.participants ?? []).some(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q)) ||
        `#${c.ticket_number}`.includes(q)
      );
    }
    return list.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [conversations, filter, search]);

  const totalUnread = conversations.reduce((s, c) => s + c.unread_count, 0);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>
            {title} 
            {totalUnread > 0 && <span style={styles.unreadBadgeTitle}>{totalUnread}</span>}
          </h1>
          <p style={styles.subtitle}>{subtitle}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filterRow}>
          {availableFilters.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              style={{
                ...styles.filterBtn,
                background: filter === opt.value ? 'var(--sc-brand-primary-bg)' : 'var(--sc-bg-secondary)',
                color: filter === opt.value ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                borderColor: filter === opt.value ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
                fontWeight: filter === opt.value ? 700 : 500,
              }}
              className="sc-press"
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search by name, subject, or ticket..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Stats Summary Cards */}
      <div style={styles.statsRow}>
        <div style={{ ...styles.statCard, borderTop: '3px solid var(--sc-brand-primary)' }}>
          <span style={styles.statNum}>{conversations.length}</span>
          <span style={styles.statLabel}>Total Chats</span>
        </div>
        <div style={{ ...styles.statCard, borderTop: '3px solid var(--sc-error)' }}>
          <span style={{ ...styles.statNum, color: 'var(--sc-error)' }}>{totalUnread}</span>
          <span style={styles.statLabel}>Unread Messages</span>
        </div>
        <div style={{ ...styles.statCard, borderTop: '3px solid var(--sc-success)' }}>
          <span style={{ ...styles.statNum, color: 'var(--sc-success)' }}>
            {conversations.filter(c => c.is_active).length}
          </span>
          <span style={styles.statLabel}>Active</span>
        </div>
        <div style={{ ...styles.statCard, borderTop: '3px solid var(--sc-warning)' }}>
          <span style={{ ...styles.statNum, color: 'var(--sc-warning)' }}>
            {conversations.filter(c => c.is_blocked).length}
          </span>
          <span style={styles.statLabel}>Blocked</span>
        </div>
      </div>

      {/* Inbox List */}
      <div style={styles.list}>
        {filtered.length === 0 ? (
          <div style={styles.empty}>
            <AnimatedIcon name="chat" size={40} color="var(--sc-text-tertiary)" />
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '12px 0 4px' }}>No messages yet</h3>
            <p style={{ fontSize: 13, color: 'var(--sc-text-secondary)', margin: 0 }}>Your ticket and request conversations will appear here.</p>
          </div>
        ) : (
          filtered.map(conv => {
            const meta = TYPE_META[conv.type] || { label: 'Ticket', icon: 'chat' as IconName, color: 'var(--sc-brand-primary)' };
            const otherParticipants = (conv.participants ?? []).filter(p => p.id !== user?.id);
            const displayName = otherParticipants.map(p => `${p.first_name} ${p.last_name}`).join(', ') || 'Support Agent';
            const isUnread = conv.unread_count > 0;

            return (
              <Link
                key={conv.id}
                to={`${basePath}/chat/room/${conv.id}`}
                style={{
                  ...styles.convRow,
                  background: isUnread ? 'var(--sc-brand-primary)' : 'var(--sc-bg-secondary)',
                  borderColor: isUnread ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
                  opacity: conv.is_blocked ? 0.55 : 1,
                  filter: conv.is_blocked ? 'grayscale(0.8)' : 'none',
                }}
                className="sc-hover-scale"
              >
                <div style={styles.convLeft}>
                  {/* Subject ticket number pill */}
                  <div style={{ ...styles.ticketBadge, background: meta.color + '22', color: meta.color }}>
                    #{conv.ticket_number}
                  </div>

                  {/* Avatar wrapper */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{ ...styles.convAvatar, background: 'var(--sc-bg-input)' }}>
                      <AnimatedIcon name={meta.icon} size={20} color={meta.color} />
                    </div>
                    {otherParticipants[0]?.online_status && (
                      <span
                        title={PRESENCE[otherParticipants[0].online_status]?.label}
                        style={{
                          ...styles.presenceDot,
                          background: PRESENCE[otherParticipants[0].online_status]?.color ?? '#CBD5E1',
                        }}
                      />
                    )}
                  </div>

                  {/* Details section */}
                  <div style={styles.convDetails}>
                    <div style={styles.convTopRow}>
                      <span style={{
                        ...styles.convName,
                        fontWeight: isUnread ? 700 : 600,
                        color: isUnread ? '#FFFFFF' : 'var(--sc-text-primary)'
                      }}>{displayName}</span>
                      <span style={{ ...styles.typePill, background: meta.color + '15', color: meta.color }}>{meta.label}</span>
                      {conv.is_blocked && <span style={styles.blockedPill}>Blocked</span>}
                    </div>
                    <div style={{
                      ...styles.convSubject,
                      fontWeight: isUnread ? 700 : 500,
                      color: isUnread ? '#FFFFFF' : 'var(--sc-text-secondary)',
                    }}>{conv.subject}</div>
                    {conv.last_message && (
                      <div style={{...styles.convPreview, color: isUnread ? 'rgba(255,255,255,0.8)' : 'var(--sc-text-tertiary)'}}>
                        <strong>{conv.last_message.sender?.first_name}:</strong> {conv.last_message.content}
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.convRight}>
                  <div style={{...styles.convTime, color: isUnread ? 'rgba(255,255,255,0.8)' : 'var(--sc-text-tertiary)'}}>
                    {conv.last_message ? relativeTime(conv.last_message.created_at) : relativeTime(conv.created_at)}
                  </div>
                  {isUnread && (
                    <div style={{...styles.unreadBadge, background: '#FFFFFF', color: 'var(--sc-brand-primary)'}}>{conv.unread_count}</div>
                  )}
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 860, margin: '0 auto', paddingBottom: 40, fontFamily: 'var(--sc-font-family)' },

  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--sc-font-display)', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '4px 0 0' },
  unreadBadgeTitle: { fontSize: 10, fontWeight: 700, background: 'var(--sc-brand-primary)', color: '#FFF', borderRadius: 9, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' },

  toolbar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
  filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filterBtn: { padding: '8px 16px', border: '1px solid var(--sc-border)', borderRadius: 20, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },
  searchInput: { flex: 1, minWidth: 220, padding: '9px 14px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13, outline: 'none', fontFamily: 'inherit', color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 },
  statCard: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)', padding: '16px 14px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 },
  statNum: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)' },
  statLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' },

  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { textAlign: 'center', padding: '60px 20px', background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },

  convRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', borderRadius: 'var(--sc-radius-lg)', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', cursor: 'pointer', transition: 'all var(--sc-transition-fast)', color: 'inherit', textDecoration: 'none' },

  convLeft: { display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 },
  ticketBadge: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap', flexShrink: 0 },
  convAvatar: { width: 42, height: 42, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  convDetails: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  convTopRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  convName: { fontSize: 14, color: 'var(--sc-text-primary)' },
  typePill: { fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '2px 8px', textTransform: 'uppercase' },
  blockedPill: { fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '2px 8px', background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', textTransform: 'uppercase' },
  convSubject: { fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  convPreview: { fontSize: 12.5, color: 'var(--sc-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 },

  presenceDot: { position: 'absolute', bottom: 1, right: 1, width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--sc-bg-secondary)', boxShadow: '0 1px 2px rgba(0,0,0,0.15)', pointerEvents: 'none' },

  convRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0, marginLeft: 12 },
  convTime: { fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 500 },
  unreadBadge: { fontSize: 10, fontWeight: 700, background: 'var(--sc-brand-primary)', color: '#FFF', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
